// src/dev/DevMemberContext.tsx
// Dev-only context for testing with member metadata
import { createContext, useContext, useState, useEffect, ReactNode } from "react";

// TestMember is the raw unsafeMetadata structure from Clerk
export interface TestMember {
  accounts?: any[];
  accountSheetUrls?: Record<string, string>;
  membership?: any;
  [key: string]: any; // Allow other metadata fields
}

// Membership override for dev testing
export interface DevMembershipOverride {
  isCustomer: boolean;
  isCommunityMember: boolean;
  isAaron: boolean;
  memberSince?: string | null;
}

// API keys override for dev testing
export interface DevUserApiKeysOverride {
  gemini?: { key: string; validated: boolean };
  openai?: { key: string; validated: boolean };
  anthropic?: { key: string; validated: boolean };
}

interface DevMemberContextType {
  isDevMode: boolean;
  activeTestMember: TestMember | null;
  testMemberName: string | null;
  setTestMember: (name: string | null) => void;
  testMembers: { name: string; data: TestMember }[];
  // Membership override for dev testing
  membershipOverride: DevMembershipOverride | null;
  setMembershipOverride: (level: "free" | "customer" | "community" | "aaron") => void;
  // API keys override for dev testing
  userApiKeysOverride: DevUserApiKeysOverride | null;
}

const DevMemberContext = createContext<DevMemberContextType | null>(null);

// Helper to check if member data has accounts (i.e., has been populated)
const hasAccounts = (data: any) => data?.accounts?.length > 0;

// Load test member data only in development using Vite's glob import
// These files only exist locally and are not committed to the repo
// The glob pattern allows missing files without breaking the build
const testMemberFiles = import.meta.glob('./test-members/*.json', { eager: true }) as Record<string, { default?: TestMember } & TestMember>;

// Extract member name from filename and build the list
const TEST_MEMBERS: { name: string; data: TestMember }[] = Object.entries(testMemberFiles)
  .map(([path, module]) => {
    // Extract name from path like "./test-members/bia.json" -> "Bia"
    const filename = path.split('/').pop()?.replace('.json', '') || '';
    const name = filename.charAt(0).toUpperCase() + filename.slice(1);
    // Handle both default export and direct export
    const data = (module.default || module) as TestMember;
    return { name, data };
  })
  .filter(m => hasAccounts(m.data));

// Helper to extract membership from test member data
function getMembershipFromTestMember(data: TestMember | null): DevMembershipOverride | null {
  if (!data) return null;
  const m = data.membership || {};
  return {
    isCustomer: Boolean(m.isCustomer),
    isCommunityMember: Boolean(m.isCommunityMember),
    isAaron: Boolean(m.isAaron),
    memberSince: m.memberSince || null,
  };
}

// Helper to extract userApiKeys from test member data
function getUserApiKeysFromTestMember(data: TestMember | null): DevUserApiKeysOverride | null {
  if (!data?.userApiKeys) return null;
  const keys = data.userApiKeys;
  return {
    gemini: keys.gemini ? { key: keys.gemini.key || "", validated: Boolean(keys.gemini.validated) } : undefined,
    openai: keys.openai ? { key: keys.openai.key || "", validated: Boolean(keys.openai.validated) } : undefined,
    anthropic: keys.anthropic ? { key: keys.anthropic.key || "", validated: Boolean(keys.anthropic.validated) } : undefined,
  };
}

export function DevMemberProvider({ children }: { children: ReactNode }) {
  const isDevMode = process.env.NODE_ENV === "development";
  const [testMemberName, setTestMemberName] = useState<string | null>(null);
  const [membershipOverride, setMembershipOverrideState] = useState<DevMembershipOverride | null>(null);

  // Persist selection in localStorage
  useEffect(() => {
    if (isDevMode) {
      const savedMember = localStorage.getItem("dev_test_member");
      const savedOverride = localStorage.getItem("dev_membership_override");

      if (savedMember) {
        setTestMemberName(savedMember);
        // Initialize membership from test member if no override saved
        if (savedOverride) {
          setMembershipOverrideState(JSON.parse(savedOverride));
        } else {
          const member = TEST_MEMBERS.find((m) => m.name === savedMember);
          if (member) {
            setMembershipOverrideState(getMembershipFromTestMember(member.data));
          }
        }
      }
    }
  }, [isDevMode]);

  const setTestMember = (name: string | null) => {
    setTestMemberName(name);
    if (name) {
      localStorage.setItem("dev_test_member", name);
      // Initialize membership override from test member
      const member = TEST_MEMBERS.find((m) => m.name === name);
      const override = getMembershipFromTestMember(member?.data || null);
      setMembershipOverrideState(override);
      if (override) {
        localStorage.setItem("dev_membership_override", JSON.stringify(override));
      }
    } else {
      localStorage.removeItem("dev_test_member");
      localStorage.removeItem("dev_membership_override");
      setMembershipOverrideState(null);
    }
    // Reload to apply the change
    window.location.reload();
  };

  const setMembershipOverride = (level: "free" | "customer" | "community" | "aaron") => {
    let override: DevMembershipOverride;
    const now = new Date().toISOString();

    switch (level) {
      case "free":
        override = { isCustomer: false, isCommunityMember: false, isAaron: false, memberSince: null };
        break;
      case "customer":
        override = { isCustomer: true, isCommunityMember: false, isAaron: false, memberSince: now };
        break;
      case "community":
        override = { isCustomer: true, isCommunityMember: true, isAaron: false, memberSince: now };
        break;
      case "aaron":
        override = { isCustomer: false, isCommunityMember: false, isAaron: true, memberSince: now };
        break;
    }

    setMembershipOverrideState(override);
    localStorage.setItem("dev_membership_override", JSON.stringify(override));
    // Force re-render without full page reload
    window.location.reload();
  };

  const activeTestMember = testMemberName
    ? TEST_MEMBERS.find((m) => m.name === testMemberName)?.data || null
    : null;

  // Get userApiKeys from active test member
  const userApiKeysOverride = testMemberName
    ? getUserApiKeysFromTestMember(activeTestMember)
    : null;

  return (
    <DevMemberContext.Provider
      value={{
        isDevMode,
        activeTestMember,
        testMemberName,
        setTestMember,
        testMembers: TEST_MEMBERS,
        membershipOverride: testMemberName ? membershipOverride : null, // Only apply override when viewing as test member
        setMembershipOverride,
        userApiKeysOverride, // Only apply when viewing as test member
      }}
    >
      {children}
    </DevMemberContext.Provider>
  );
}

export function useDevMember() {
  const context = useContext(DevMemberContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      isDevMode: false,
      activeTestMember: null,
      testMemberName: null,
      setTestMember: () => {},
      testMembers: [],
      membershipOverride: null,
      setMembershipOverride: () => {},
      userApiKeysOverride: null,
    };
  }
  return context;
}
